import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { Button, Col, DatePicker, Form, Row, Table } from "antd";
import { getReportDescription } from "../../api/APIDescriptionReport";
import DownloadIcon from "../../images/IconDownload.svg";
import { downloadReportDescription } from "../../api/APIDescriptionReport";
import { openNotificationWithIcon } from "../../Function";
import TotalRowAllFilter from "./TotalRowAllFilter";
import TotalRowAllReport from "./TotalRowAllReport";
import CurrencyTotalRows from "./CurrencyTotalRows";
import "./DescriptionReport.css";


export default function DescriptionReport() {
    const userInfo = useSelector((state) => state.getUserInfo.userInfo);
    const [lsPermissions, setLsPermissions] = useState([]);

    // State management
    const [valueMonth, setValueMonth] = useState(dayjs().month() + 1);
    const [valueYear, setValueYear] = useState(dayjs().year());
    const [dataReportDescription, setDataReportDescription] = useState([]);
    const [dataTotalReportDescription, setDataTotalReportDescription] = useState([]);
    const [loadingConfirm, setLoadingConfirm] = useState(false);
    const [loadingDownload, setLoadingDownload] = useState(false);

    // State show/hide column
    const [showAllColumns, setShowAllColumns] = useState(false);
    const [onOpenColumns, setOnOpenColumns] = useState(false)
    const [titleBtn, setTitleBtn] = useState("VIEW ALL")

    // State filter
    const [currentClientCodeFilter, setCurrentClientCodeFilter] = useState([]);
    const [currentProjectNameFilter, setCurrentProjectNameFilter] = useState([]);
    const [valueClientCodeFilter, setValueClientCodeFilter] = useState([])
    const [valueProjectNameFilter, setValueProjectNameFilter] = useState([])

    // console.log("data: ", dataReportDescription);

    // State pagination mặc định 100
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 100,
        total: 0
    });

    // Lấy dữ liệu theo trang
    const getCurrentPageData = (data, current, pageSize) => {
        const startIndex = (current - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        return data.slice(startIndex, endIndex);
    };

    // Filter all data first
    const allFilteredData = useMemo(() => {
        return dataReportDescription.filter(item => {
            const clientMatch = currentClientCodeFilter.length === 0 ||
                currentClientCodeFilter.some(value => item.ClientCode.startsWith(value));
            const projectMatch = currentProjectNameFilter.length === 0 ||
                currentProjectNameFilter.some(value => item.ProjectName.startsWith(value));
            return clientMatch && projectMatch;
        });
    }, [dataReportDescription, currentClientCodeFilter, currentProjectNameFilter]);

    // Get current page data
    const currentPageData = useMemo(() => {
        return getCurrentPageData(allFilteredData, pagination.current, pagination.pageSize);
    }, [allFilteredData, pagination.current, pagination.pageSize]);

    // Tính toán dữ liệu đã lọc cho total
    const filteredData = useMemo(() => {
        const filtered = dataReportDescription.filter(item => {
            const clientMatch = currentClientCodeFilter.length === 0 ||
                currentClientCodeFilter.some(value => item.ClientCode.startsWith(value));
            const projectMatch = currentProjectNameFilter.length === 0 ||
                currentProjectNameFilter.some(value => item.ProjectName.startsWith(value));
            return clientMatch && projectMatch;
        });

        // Update total count in pagination
        setPagination(prev => ({
            ...prev,
            total: filtered.length
        }));

        return filtered;
    }, [dataReportDescription, currentClientCodeFilter, currentProjectNameFilter]);

    // Tính toán tên dự án có sẵn dựa trên ClientCode đã chọn
    const availableProjectNames = useMemo(() => {
        const uniqueProjectNames = [...new Set(filteredData.map(item => item.ProjectName))];
        return uniqueProjectNames.map(name => ({
            text: name,
            value: name
        }));
    }, [filteredData]);

    // Các cột tính tổng
    const totalColumns = [
        'Quantity01_20', 'Serviceamount1_20', 'RevenueExcludingTaxJPY01_20',
        'Quantity21_31', 'Serviceamount21_31', 'RevenueExcludingTaxJPY21_31',
        'QuantityTOTAL', 'ServiceamountTOTAL', 'RevenueExcludingTaxJPYTOTAL',
        'RSVNtoRSJPUnitPrice', 'RSVNtoRSJPQuantity01_20', 'RSVNtoRSJPServiceamount1_20',
        'RSVNtoRSJPRevenueExcludingTax01_20', 'RSVNtoRSJPQuantity21_31', 'RSVNtoRSJPServiceamount21_31',
        'RSVNtoRSJPRevenueExcludingTax21_31', 'RSVNtoRSJPQuantityTOTAL', 'RSVNtoRSJPServiceamountTOTAL',
        'RSVNtoRSJPRevenueExcludingTaxTOTAL'
    ]

    // Tạo mảng các cột cần tính tổng
    const totals = useMemo(() => {
        return totalColumns.reduce((acc, col) => {
            acc[col] = currentPageData.reduce(
                (sum, item) => sum + (Number(item[col]) || 0),
                0
            );
            return acc;
        }, {});
    }, [currentPageData, totalColumns]);

    // Hàm xử lý thay đổi Filter
    const handleTableChange = (paginationConfig, filters) => {
        // Cập nhật filter
        setCurrentClientCodeFilter(filters.ClientCode || []);
        setCurrentProjectNameFilter(filters.ProjectName || []);

        // Cập nhật pagination với pageSize mới
        setPagination(prev => ({
            ...prev,
            current: paginationConfig.current,
            pageSize: paginationConfig.pageSize || 100,  // Nếu không có giá trị thì lấy mặc định là 100
            total: allFilteredData.length
        }));
    };

    // Cập nhật pagination total bất cứ khi nào dữ liệu thay đổi
    useEffect(() => {
        setPagination(prev => ({
            ...prev,
            total: allFilteredData.length
        }));
    }, [allFilteredData]);

    const getClientCodeFilters = (dataFilter) => {
        if (dataFilter !== undefined) {
            const clientCodes = [...new Set(dataFilter.map(item => item.ClientCode))];
            const filters = clientCodes.map(code => ({
                text: code,
                value: code,
            }));
            setValueClientCodeFilter(filters);
        }
    };

    const getProjectNameFilters = (dataFilter) => {
        if (dataFilter !== undefined) {
            const projectNames = [...new Set(dataFilter.map(item => item.ProjectName))];
            const filters = projectNames.map(name => ({
                text: name,
                value: name,
            }));
            setValueProjectNameFilter(filters);
        }
    };

    useEffect(() => {
        if (userInfo) {
            const arrPermission = userInfo.permission.map((item, index) => {
                return item.name;
            });

            if (arrPermission !== undefined) {
                setLsPermissions([...arrPermission]);
            }
        }

    }, [userInfo]);

    useEffect(() => {
        if (dataReportDescription.length > 0) {
            // Set client code filters
            const clientCodes = [...new Set(dataReportDescription.map(item => item.ClientCode))];
            setValueClientCodeFilter(clientCodes.map(code => ({
                text: code,
                value: code
            })));
        }
    }, [dataReportDescription]);

    const formatNumber = (number) => {
        if (number == null || number === "") return "";
        return Number.isInteger(number) ? number.toLocaleString('en-US') : Number(number).toFixed(3).toLocaleString('en-US');
    };

    const createNumberColumn = (title, dataIndex, width = 120, classNameStr) => ({
        title: <p style={{ textAlign: "center" }}>{title}</p>,
        dataIndex,
        key: dataIndex,
        width,
        render: (value) => formatNumber(value),
        align: "right",
        className: classNameStr,
    });

    // Tô màu các nhóm cột theo className
    const reportColumns = [
        { title: "No.", key: "index", fixed: "left", width: 40, render: (_, __, index) => index + 1, },
        { title: "Close Date", dataIndex: "CloseDate", key: "CloseDate", width: 100, hide: !showAllColumns, fixed: "left" },
        {
            title: "Client Code", dataIndex: "ClientCode", key: "ClientCode", width: 120,
            filters: valueClientCodeFilter, // Sử dụng giá trị từ filterValues
            filterSearch: true,
            onFilter: (value, record) => record.ClientCode.startsWith(value),
            fixed: "left"
        },
        {
            title: "Project Name", dataIndex: "ProjectName", key: "ProjectName", width: 120,
            filters: availableProjectNames,
            filterSearch: true,
            onFilter: (value, record) => record.ProjectName.startsWith(value),
            fixed: "left"
        },
        { title: "Bank Account", dataIndex: "BankAccount", key: "BankAccount", width: 120, hide: !showAllColumns, fixed: "left" },
        { title: "Currency", dataIndex: "Currency", key: "Currency", width: 80, hide: !showAllColumns, fixed: "left" },
        { title: "Department", dataIndex: "Department", key: "Department", width: 100, hide: !showAllColumns, fixed: "left" },
        { title: "Product/Service", dataIndex: "Product/Service", key: "Product/Service", width: 120, hide: !showAllColumns, fixed: "left" },
        { title: "Owner", dataIndex: "Owner", key: "Owner", width: 80, hide: !showAllColumns, fixed: "left" },
        {
            title: "Project Stage", dataIndex: "ProjectStage", key: "ProjectStage", width: 120, fixed: "left",

        },
        { title: "Sub-project Name 1", dataIndex: "Sub-projectName1", key: "Sub-projectName1", width: 150, fixed: "left" },
        { title: "Sub-project Name 2", dataIndex: "Sub-projectName2", key: "Sub-projectName2", width: 150, fixed: "left" },
        { title: "Project Description", dataIndex: "ProjectDescription", key: "ProjectDescription", width: 150, fixed: "left" },
        { title: "Pricing Models", dataIndex: "Pricingmodels", key: "Pricingmodels", width: 150, fixed: "left" },

        createNumberColumn("Unit Price", "UnitPrice", 100, "light-blue-unit-price"),
        createNumberColumn("Quantity 1-20", "Quantity01_20", 120, "light-blue-qsr-one-twenty"),
        createNumberColumn("Service Amount 1-20", "Serviceamount1_20", 120, "light-blue-qsr-one-twenty"),
        createNumberColumn("Revenue Excl. Tax JPY 1-20", "RevenueExcludingTaxJPY01_20", 120, "light-blue-qsr-one-twenty"),

        createNumberColumn("Quantity 21-31", "Quantity21_31", 120, "light-blue-qsr-twenty-end"),
        createNumberColumn("Service Amount 21-31", "Serviceamount21_31", 120, "light-blue-qsr-twenty-end"),
        createNumberColumn("Revenue Excl. Tax JPY 21-31", "RevenueExcludingTaxJPY21_31", 120, "light-blue-qsr-twenty-end"),

        createNumberColumn("Quantity Total", "QuantityTOTAL", 120, "light-blue-qsr-total"),
        createNumberColumn("Service Amount Total", "ServiceamountTOTAL", 120, "light-blue-qsr-total"),
        createNumberColumn("Revenue Excl. Tax JPY Total", "RevenueExcludingTaxJPYTOTAL", 120, "light-blue-qsr-total"),

        createNumberColumn("RSVN to RSJP Unit Price", "RSVNtoRSJPUnitPrice", 120, "light-red-rsvn-to-rsjp-unit-price"),
        createNumberColumn("RSVN to RSJP Quantity 1-20", "RSVNtoRSJPQuantity01_20", 120, "light-red-rsvn-to-rsjp-qsr-one-twenty"),
        createNumberColumn("RSVN to RSJP Service Amount 1-20", "RSVNtoRSJPServiceamount1_20", 120, "light-red-rsvn-to-rsjp-qsr-one-twenty"),
        createNumberColumn("RSVN to RSJP Revenue Excl. Tax 1-20", "RSVNtoRSJPRevenueExcludingTax01_20", 120, "light-red-rsvn-to-rsjp-qsr-one-twenty"),

        createNumberColumn("RSVN to RSJP Quantity 21-31", "RSVNtoRSJPQuantity21_31", 120, "light-red-rsvn-to-rsjp-qsr-twenty-end"),
        createNumberColumn("RSVN to RSJP Service Amount 21-31", "RSVNtoRSJPServiceamount21_31", 120, "light-red-rsvn-to-rsjp-qsr-twenty-end"),
        createNumberColumn("RSVN to RSJP Revenue Excl. Tax 21-31", "RSVNtoRSJPRevenueExcludingTax21_31", 120, "light-red-rsvn-to-rsjp-qsr-twenty-end"),

        createNumberColumn("RSVN to RSJP Quantity Total", "RSVNtoRSJPQuantityTOTAL", 120, "light-red-rsvn-to-rsjp-qsr-total"),
        createNumberColumn("RSVN to RSJP Service Amount Total", "RSVNtoRSJPServiceamountTOTAL", 120, "light-red-rsvn-to-rsjp-qsr-total"),
        createNumberColumn("RSVN to RSJP Revenue Excl. Tax Total", "RSVNtoRSJPRevenueExcludingTaxTOTAL", 120, "light-red-rsvn-to-rsjp-qsr-total"),
    ];

    const handleChangeDate = (valueDate) => {
        if (valueDate) {
            setValueMonth(valueDate.month() + 1);
            setValueYear(valueDate.year());
        }
    };

    const handleConfirm = () => {
        setLoadingConfirm(true);
        const params = {
            month: valueMonth,
            year: valueYear
        };
        getReportDescription(params)
            .then((res) => {
                const objData = res?.data?.message
                const objTotal = res?.data?.total
                if (objData) {
                    setDataReportDescription(objData);
                    setDataTotalReportDescription(objTotal);
                }
                getClientCodeFilters(res?.data?.message)
                getProjectNameFilters(res?.data?.message)
            })
            .catch((error) => {
                console.error("Error:", error);
            });
        setLoadingConfirm(false);
    };

    const handleDownload = () => {
        setLoadingDownload(true);
        const params = {
            month: valueMonth,
            year: valueYear,
            message: "download",
        };

        downloadReportDescription(params)
            .then((res) => {
                // Lấy content type từ header hoặc mặc định
                const contentType = res.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

                // Tạo Blob từ dữ liệu
                const blob = new Blob([res.data], { type: contentType });
                const downloadUrl = window.URL.createObjectURL(blob);

                // Tạo thẻ a ẩn để download
                const link = document.createElement('a');
                link.href = downloadUrl;

                // tạo tên file
                let fileName = `data_${valueMonth.toString().replace('0', '')}_${valueYear}.xlsx`;

                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(downloadUrl);
            })
            .catch((error) => {
                console.error("Error:", error);
                openNotificationWithIcon("error", error.response?.data?.message || "Download failed");
            })
            .finally(() => {
                setLoadingDownload(false);
            });
    };

    const toggleColumnsVisibility = () => {
        setShowAllColumns(prev => !prev);
        setOnOpenColumns(!onOpenColumns)
        if (onOpenColumns === true) {
            setTitleBtn("VIEW ALL")
        } else {
            setTitleBtn("HIDDEN")
        }

    };

    // Lọc các cột hiển thị dựa vào showAllColumns
    const filteredColumns = reportColumns.filter((column) => {
        if (column.hide) {
            return showAllColumns;
        }
        return true;
    });

    const calculateAllDataTotals = () => {
        const result = {};
        totalColumns.forEach(column => {
            result[column] = dataReportDescription.reduce((sum, item) => {
                return sum + (parseFloat(item[column]) || 0);
            }, 0);
        });
        return result;
    };

    const allDataTotals = calculateAllDataTotals();


    return (
        <div style={{ position: "relative", width: "100%" }}>
            <div style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "7vh",
            }}>
                <div className="get-detail-pj">
                    <Form layout="vertical" className="custom-form">
                        <Row className="params-pj" 
                        style={{ 
                            paddingLeft: "2%", 
                            justifyContent: "flex-start",
                            height: "7vh",
                            width: "100%",
                            display: "flex",
                            gap: "16px",
                            alignItems: "center",
                            }} gutter={16}>
                            <Col span={5}>
                                <Form.Item
                                    name="date"
                                    label={<p className="title-detail-pj">Tháng dự án</p>}
                                    key="date"
                                >
                                    <DatePicker
                                        style={{
                                            width: "100%",
                                        }}
                                        allowClear={false}
                                        picker="month"
                                        format={"MM-YYYY"}
                                        defaultValue={dayjs()}
                                        onChange={handleChangeDate}
                                    />
                                </Form.Item>
                            </Col>

                            <Col style={{ flex: "0 0 auto" }}>
                                <Button
                                    htmlType="submit"
                                    id="btn-confirm"
                                    loading={loadingConfirm}
                                    style={{
                                        color: "#fff",
                                        backgroundColor: "#5465EF",
                                        width: "100%",
                                    }}
                                    onClick={handleConfirm}
                                    className="btn-revenue"
                                >
                                    CONFIRM
                                </Button>
                            </Col>
                            {lsPermissions.includes("View full Operation") === true && dataReportDescription.length > 0 && (
                                <Col span={5} style={{ flex: "1" }}>
                                    <Button
                                        className={
                                            dataReportDescription.length > 0
                                                ? "btn-revenue"
                                                : "btn-downloadFile"
                                        }
                                        onClick={toggleColumnsVisibility}
                                        style={{ color: "#337aee", width: "100%" }}
                                    >
                                        {titleBtn}
                                    </Button>
                                </Col>
                            )}

                            {dataReportDescription.length > 0 && (
                                <Col span={5} style={{ flex: "1" }}>
                                    <Button
                                        htmlType="button"
                                        loading={loadingDownload}
                                        className="btn-downloadFile"
                                        onClick={handleDownload}
                                        style={{ width: "100%" }}
                                    >
                                        DOWNLOAD FILE
                                        <img
                                            style={{ marginLeft: 8 }}
                                            src={DownloadIcon}
                                            alt=""
                                        ></img>
                                    </Button>
                                </Col>
                            )}
                        </Row>
                    </Form>

                   
                </div>
            </div>

            <div className="table-sale-ope" style={{ marginTop: "2%" }}>
                <Table
                    columns={filteredColumns}
                    dataSource={currentPageData}
                    sticky   // Enable sticky header
                    style={{ width: "98%" }}
                    scroll={{
                        x: 3000,
                        y: window.screen.availHeight > 850 ? "56vh" : "53vh",
                    }}
                    className="custom-table table-striped-rows"
                    bordered
                    rowClassName={(record, index) => {
                        let className = index % 2 === 0 ? "table-row-light" : "table-row-dark";
                        if (record.ProjectStage === 'Waiting') {
                            return className + " row-waiting";
                        } if (record.ProjectStage === 'Close') {
                            return className + " row-close";
                        }
                        return className;
                    }}
                    pagination={{
                        ...pagination,
                        defaultPageSize: 100,
                        pageSizeOptions: ['10', '20', '30', '50', '100'],
                        showSizeChanger: true,
                    }}
                    onChange={handleTableChange}
                    summary={() => (
                        <Table.Summary fixed>

                            <CurrencyTotalRows
                                filteredColumns={filteredColumns}
                                totalColumns={totalColumns}
                                currencyTotals={dataTotalReportDescription}
                                formatNumber={formatNumber}
                            />

                            <TotalRowAllFilter
                                filteredColumns={filteredColumns}
                                totalColumns={totalColumns}
                                totals={totals}
                                formatNumber={formatNumber}
                            />

                            <TotalRowAllReport
                                filteredColumns={filteredColumns}
                                totalColumns={totalColumns}
                                allDataTotals={allDataTotals}
                                formatNumber={formatNumber}
                            />

                        </Table.Summary>
                    )}

                />
            </div>
        </div>
    );
}